import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { listContracts } from "@/services/contracts.service";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtMoney, fmtDate, statusColor, statusLabel } from "@/lib/format";
import { Plus, Search, HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { usePolling } from "@/lib/usePolling";

const STATUS_MAP = {
  "needs-funding": ["Needs funding", "Partially funded"],
  "pending-approval": ["Pending approval"],
  "ready-to-release": ["Ready to release"],
  "in-dispute": ["In dispute"],
  "awaiting-acceptance": ["Awaiting acceptance"],
  released: ["Released"],
  active: ["Needs funding", "Partially funded", "In progress", "Pending approval", "Ready to release"],
  "reserved-payout": ["Partially funded", "In progress", "Pending approval", "Ready to release"],
};

const STATUS_OPTIONS = [
  { v: "", l: "All statuses" },
  { v: "active", l: "Active" },
  { v: "needs-funding", l: "Needs funding" },
  { v: "pending-approval", l: "Pending approval" },
  { v: "ready-to-release", l: "Ready to release" },
  { v: "in-dispute", l: "In dispute" },
  { v: "awaiting-acceptance", l: "Awaiting acceptance" },
  { v: "released", l: "Released" },
];

const RoleChip = ({ c }) => {
  if (c.is_payer && c.is_payee) {
    return (
      <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.15em] bg-primary/10 text-primary border border-primary/20">
        BOTH
      </span>
    );
  }
  if (c.is_payer) {
    return (
      <span
        className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.15em] bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-900/60"
        data-testid="role-chip-payer"
      >
        PAYER
      </span>
    );
  }
  if (c.is_payee) {
    return (
      <span
        className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.15em] bg-emerald-100 text-emerald-800 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-900/60"
        data-testid="role-chip-payee"
      >
        PAYEE
      </span>
    );
  }
  return null;
};

const whoIsWaiting = (c, user) => {
  // Map unified_status -> side that must act
  const s = c.unified_status;
  if (!user) return null;
  if (s === "Awaiting acceptance") {
    return c.created_by_user_id === user.user_id ? "counterparty" : "you";
  }
  if (s === "Needs funding") return c.is_payer ? "you" : "counterparty";
  if (s === "In progress") return c.is_payee ? "you" : "counterparty"; // payee to submit
  if (s === "Pending approval") return c.is_payer ? "you" : "counterparty";
  if (s === "Ready to release") return c.is_payer ? "you" : "counterparty";
  if (s === "In dispute") return "resolution";
  return null;
};

const Contracts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter =
    searchParams.get("filter") === "paying"
      ? "paying"
      : searchParams.get("filter") === "receiving"
        ? "receiving"
        : "all";
  const initialStatus = searchParams.get("status") || "";
  const [roleFilter, setRoleFilter] = useState(initialFilter); // all | paying | receiving
  const [statusFilter, setStatusFilter] = useState(initialStatus); // "", needs-funding, pending-approval, ready-to-release

  const load = async () => {
    try {
      setContracts(await listContracts());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  usePolling(load);

  const setStatus = (v) => {
    setStatusFilter(v);
    const params = {};
    if (roleFilter !== "all") params.filter = roleFilter;
    if (v) params.status = v;
    setSearchParams(params);
  };

  const filtered = useMemo(() => {
    return contracts.filter((c) => {
      if (roleFilter === "paying" && !c.is_payer) return false;
      if (roleFilter === "receiving" && !c.is_payee) return false;
      if (statusFilter && STATUS_MAP[statusFilter] && !STATUS_MAP[statusFilter].includes(c.unified_status))
        return false;
      if (!q) return true;
      const needle = q.toLowerCase();
      return (
        c.title.toLowerCase().includes(needle) ||
        (c.freelancer_email || "").toLowerCase().includes(needle) ||
        (c.business_name || "").toLowerCase().includes(needle)
      );
    });
  }, [contracts, q, roleFilter, statusFilter]);

  const pillBase = "px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border";
  const pillActive = "bg-primary text-primary-foreground border-primary";
  const pillIdle = "bg-transparent text-muted-foreground border-border hover:text-foreground";

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            Contracts
          </div>
          <h1 className="inline-flex items-center gap-2 mt-1 text-3xl font-bold tracking-tight sm:text-4xl">
            All contracts
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="status-glossary-btn"
                >
                  <HelpCircle className="w-5 h-5" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="text-sm w-80" data-testid="status-glossary-popover">
                <div className="mb-2 font-semibold font-display">Status glossary</div>
                <dl className="space-y-1.5 text-xs">
                  <div>
                    <dt className="inline font-semibold">Contract · </dt>
                    <dd className="inline text-muted-foreground">
                      Draft → Awaiting acceptance → Active → Completed / Cancelled
                    </dd>
                  </div>
                  <div>
                    <dt className="inline font-semibold">Milestone · </dt>
                    <dd className="inline text-muted-foreground">
                      Draft (not funded) → Funded → Submitted → Approved → Released (paid). Can be Disputed at
                      any live stage.
                    </dd>
                  </div>
                  <div>
                    <dt className="inline font-semibold">Dispute · </dt>
                    <dd className="inline text-muted-foreground">Open → Under review → Resolved</dd>
                  </div>
                </dl>
                <div className="mt-3 text-[11px] text-muted-foreground">
                  Contract pills like “Needs funding” / “Pending approval” / “Ready to release” roll up the
                  most urgent milestone on the contract.
                </div>
              </PopoverContent>
            </Popover>
          </h1>
        </div>
        {!isAdmin && (
          <Link to="/contracts/new">
            <Button data-testid="contracts-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New contract
            </Button>
          </Link>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
          <Input
            placeholder="Search contracts…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-10 pl-9"
            data-testid="contracts-search-input"
          />
        </div>
        {!isAdmin && (
          <div
            className="inline-flex items-center gap-1 p-1 border rounded-full bg-secondary/50 border-border"
            role="tablist"
          >
            <button
              type="button"
              onClick={() => setRoleFilter("all")}
              className={`${pillBase} ${roleFilter === "all" ? pillActive : pillIdle}`}
              data-testid="filter-all-btn"
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setRoleFilter("paying")}
              className={`${pillBase} ${roleFilter === "paying" ? pillActive : pillIdle}`}
              data-testid="filter-paying-btn"
            >
              I'm paying
            </button>
            <button
              type="button"
              onClick={() => setRoleFilter("receiving")}
              className={`${pillBase} ${roleFilter === "receiving" ? pillActive : pillIdle}`}
              data-testid="filter-receiving-btn"
            >
              I'm getting paid
            </button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatus(e.target.value)}
            className={`h-10 rounded-lg border bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring ${statusFilter ? "border-primary text-primary font-medium" : "border-border text-muted-foreground"}`}
            data-testid="status-filter-select"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>
                {o.v ? `Status: ${o.l}` : o.l}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto border bg-card border-border rounded-xl">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="contracts-empty">
            No contracts found.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs tracking-widest uppercase bg-secondary/40 text-muted-foreground">
              <tr>
                <th className="px-6 py-3 font-semibold text-left">Title</th>
                <th className="hidden px-6 py-3 font-semibold text-left md:table-cell">Counterparty</th>
                <th className="px-6 py-3 font-semibold text-right">Total</th>
                <th className="hidden px-6 py-3 font-semibold text-left sm:table-cell">Created</th>
                <th className="px-6 py-3 font-semibold text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((c) => (
                <tr
                  key={c.contract_id}
                  onClick={() => navigate(`/contracts/${c.contract_id}`)}
                  className="transition-colors cursor-pointer hover:bg-secondary/30"
                  data-testid={`contract-row-${c.contract_id}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/contracts/${c.contract_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium hover:text-primary"
                        data-testid={`contract-link-${c.contract_id}`}
                      >
                        {c.title}
                      </Link>
                      <RoleChip c={c} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {c.milestone_count} milestone{c.milestone_count === 1 ? "" : "s"}
                      {c.funded_count !== undefined && c.milestone_count > 0 && (
                        <>
                          {" "}
                          · {c.funded_count}/{c.milestone_count} funded
                        </>
                      )}
                      {(() => {
                        const wait = whoIsWaiting(c, user);
                        return wait ? (
                          <>
                            {" "}
                            · <span className="font-medium text-foreground">Waiting on {wait}</span>
                          </>
                        ) : null;
                      })()}
                    </div>
                  </td>
                  <td className="hidden px-6 py-4 text-muted-foreground md:table-cell">
                    {c.is_payer ? c.freelancer_name || c.freelancer_email : c.business_name}
                  </td>
                  <td className="px-6 py-4 font-semibold text-right font-display">
                    {fmtMoney(c.total_amount, c.currency)}
                  </td>
                  <td className="hidden px-6 py-4 text-muted-foreground sm:table-cell">
                    {fmtDate(c.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColor(c.unified_status || c.status)}`}
                    >
                      {c.unified_status || statusLabel(c.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Contracts;
